import { forwardRef, useState, type ReactNode } from 'react'
import { cn } from '../utils'

interface TabsProps {
  children: ReactNode
  className?: string
}

interface TabsListProps {
  children: ReactNode
  className?: string
}

interface TabsTriggerProps {
  value: string
  active?: boolean
  onClick: () => void
  children: ReactNode
  icon?: ReactNode
  className?: string
  style?: React.CSSProperties
  title?: string
  'data-testid'?: string
}

interface TabsContentProps {
  value: string
  active?: boolean
  children: ReactNode
  className?: string
}

export function Tabs({ children, className }: TabsProps) {
  return <div className={cn('flex flex-col', className)}>{children}</div>
}

export function TabsList({ children, className }: TabsListProps) {
  return (
    <div className={cn('flex items-center border-b border-border', className)}>
      {children}
    </div>
  )
}

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(function TabsTrigger({
  active,
  onClick,
  children,
  icon,
  className,
  style,
  title,
  value,
  'data-testid': dataTestId,
}, ref) {
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={cn(
        'flex items-center justify-center px-3 py-2 transition-colors border-b-2',
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground',
        className
      )}
      style={style}
      title={title}
      data-tab-value={value}
      data-testid={dataTestId}
      aria-label={title}
    >
      {icon && <span className="flex items-center justify-center">{icon}</span>}
      {children}
    </button>
  )
})

export function TabsContent({ active, children, className }: TabsContentProps) {
  if (!active) return null
  return <div className={cn('flex-1 overflow-auto', className)}>{children}</div>
}

// Hook for managing tab state
export function useTabs(defaultValue: string) {
  const [activeTab, setActiveTab] = useState(defaultValue)
  return { activeTab, setActiveTab }
}
